import { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { images } from './data';
import './index.scss';

// ==================== 场景配置 ====================

interface SceneConfig {
    label: string;
    cameraTarget: { x: number; y: number; z: number };
}

const SCENE_CONFIG: Record<string, SceneConfig> = {
    livingRoom: { label: '客厅', cameraTarget: { x: 0, y: 0, z: 0.01 } },
    balcony: { label: '阳台', cameraTarget: { x: 0, y: 0, z: -10 } },
    kitchen: { label: '厨房', cameraTarget: { x: 0, y: 0, z: 10 } },
};

// 所有场景图片总数 = 3 场景 × 6 张
const TOTAL_IMAGES = 18;

// ==================== THREE 工具函数 ====================

/**
 * 创建全景立方体（BackSide 渲染，相机在内部可看到贴图）
 * 复用同一 BoxGeometry 实例减少 GPU 内存
 */
function createPanoramaCube(
    textureLoader: THREE.TextureLoader,
    imageList: string[],
    sharedGeometry: THREE.BoxGeometry,
    position: THREE.Vector3 = new THREE.Vector3(0, 0, 0),
): THREE.Mesh {
    const materials = imageList.map((item) => {
        const texture = textureLoader.load(item);
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        return new THREE.MeshBasicMaterial({ map: texture, side: THREE.BackSide });
    });
    const mesh = new THREE.Mesh(sharedGeometry, materials);
    mesh.position.copy(position);
    mesh.frustumCulled = false;
    return mesh;
}

/**
 * 创建 WebGL 渲染器并挂载到容器
 */
function createRenderer(container: HTMLDivElement): THREE.WebGLRenderer {
    const renderer = new THREE.WebGLRenderer({ alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    container.appendChild(renderer.domElement);
    return renderer;
}

/**
 * 注册窗口缩放监听
 */
function addResizeListener(
    camera: THREE.PerspectiveCamera,
    renderer: THREE.WebGLRenderer,
): () => void {
    const handleResize = () => {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
}

/**
 * 鼠标拖拽旋转控制器
 */
function addDragRotateControl(
    container: HTMLElement,
    camera: THREE.PerspectiveCamera,
): () => void {
    let isMouseDown = false;
    camera.rotation.order = 'YXZ';

    const onMouseDown = () => { isMouseDown = true; };
    const onMouseUp = () => { isMouseDown = false; };
    const onMouseOut = () => { isMouseDown = false; };

    const onMouseMove = (event: MouseEvent) => {
        if (!isMouseDown) return;
        camera.rotation.x += event.movementY * 0.005;
        camera.rotation.y += event.movementX * 0.005;
    };

    container.addEventListener('mousedown', onMouseDown, false);
    window.addEventListener('mouseup', onMouseUp, false);
    window.addEventListener('mouseout', onMouseOut, false);
    window.addEventListener('mousemove', onMouseMove, false);

    return () => {
        container.removeEventListener('mousedown', onMouseDown);
        window.removeEventListener('mouseup', onMouseUp);
        window.removeEventListener('mouseout', onMouseOut);
        window.removeEventListener('mousemove', onMouseMove);
    };
}

/**
 * 鼠标滚轮缩放控制器
 */
function addWheelZoomControl(
    container: HTMLElement,
    targetZoom: { value: number },
): () => void {
    const MIN_ZOOM = 0.5;
    const MAX_ZOOM = 3;

    const onWheel = (event: WheelEvent) => {
        event.preventDefault();
        const delta = event.deltaY > 0 ? -0.3 : 0.3;
        targetZoom.value = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, targetZoom.value + delta));
    };

    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
}

/**
 * 启动渲染循环
 */
function startAnimationLoop(
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    renderer: THREE.WebGLRenderer,
    targetZoom: { value: number },
): void {
    renderer.setAnimationLoop(() => {
        if (Math.abs(camera.zoom - targetZoom.value) > 0.001) {
            camera.zoom += (targetZoom.value - camera.zoom) * 0.12;
            camera.updateProjectionMatrix();
        }
        renderer.render(scene, camera);
    });
}

/**
 * 平滑过渡相机到目标位置
 */
function animateCameraTo(
    camera: THREE.PerspectiveCamera,
    targetPos: { x: number; y: number; z: number },
    duration: number = 800,
): void {
    const startPos = { x: camera.position.x, y: camera.position.y, z: camera.position.z };
    const startTime = performance.now();

    const tick = () => {
        const elapsed = performance.now() - startTime;
        const t = Math.min(elapsed / duration, 1);
        const ease = 1 - Math.pow(1 - t, 3);

        camera.position.x = startPos.x + (targetPos.x - startPos.x) * ease;
        camera.position.y = startPos.y + (targetPos.y - startPos.y) * ease;
        camera.position.z = startPos.z + (targetPos.z - startPos.z) * ease;

        if (t < 1) requestAnimationFrame(tick);
    };
    tick();
}

/**
 * 销毁场景资源
 */
function disposeScene(scene: THREE.Scene): void {
    const disposedGeometries = new Set<THREE.BufferGeometry>();
    scene.traverse((child) => {
        if (child instanceof THREE.Mesh) {
            if (!disposedGeometries.has(child.geometry)) {
                child.geometry.dispose();
                disposedGeometries.add(child.geometry);
            }
            if (Array.isArray(child.material)) {
                child.material.forEach((m) => {
                    if (m.map) m.map.dispose();
                    m.dispose();
                });
            } else {
                if (child.material.map) child.material.map.dispose();
                child.material.dispose();
            }
        }
    });
}

// ==================== 主组件 ====================

const VR: React.FC = () => {
    const [currentScene, setCurrentScene] = useState<string>('livingRoom');
    const [loaded, setLoaded] = useState(false);
    const [progress, setProgress] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);

    const handleNavClick = useCallback(
        (sceneKey: string) => {
            const config = SCENE_CONFIG[sceneKey];
            if (!config || !cameraRef.current) return;
            setCurrentScene(sceneKey);
            animateCameraTo(cameraRef.current, config.cameraTarget);
        },
        [],
    );

    useEffect(() => {
        // 场景与相机
        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
        camera.position.set(0, 0, 0.01);
        cameraRef.current = camera;

        // 渲染器
        if (!containerRef.current) return;
        const renderer = createRenderer(containerRef.current);

        // 窗口自适应
        const removeResize = addResizeListener(camera, renderer);
        const removeDrag = addDragRotateControl(renderer.domElement, camera);
        const targetZoom = { value: 1 };
        const removeWheel = addWheelZoomControl(renderer.domElement, targetZoom);

        // 使用 LoadingManager 追踪 18 张贴图加载进度
        const manager = new THREE.LoadingManager();
        let loadedCount = 0;

        manager.onProgress = (_url, _loaded, total) => {
            loadedCount = Math.max(loadedCount, total);
            setProgress(Math.round((loadedCount / TOTAL_IMAGES) * 100));
        };

        manager.onLoad = () => {
            setLoaded(true);
            // 贴图加载完成后启动渲染循环
            startAnimationLoop(scene, camera, renderer, targetZoom);
        };

        manager.onError = () => {
            // 即使有加载失败也放行，避免白屏
            setLoaded(true);
            startAnimationLoop(scene, camera, renderer, targetZoom);
        };

        // 创建带 LoadingManager 的加载器
        const loader = new THREE.TextureLoader(manager);
        const boxGeometry = new THREE.BoxGeometry(10, 10, 10);

        // 开始加载所有贴图（添加场景）
        scene.add(createPanoramaCube(loader, images.livingRoomList, boxGeometry));
        scene.add(createPanoramaCube(loader, images.balconyList, boxGeometry, new THREE.Vector3(0, 0, -10)));
        scene.add(createPanoramaCube(loader, images.kitchenList, boxGeometry, new THREE.Vector3(0, 0, 10)));

        // 清理
        return () => {
            removeResize();
            removeDrag();
            removeWheel();
            renderer.setAnimationLoop(null);
            disposeScene(scene);
            renderer.dispose();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="vr-container">
            {/* 加载遮罩 */}
            {!loaded && (
                <div className="vr-loading">
                    <div className="vr-loading-spinner" />
                    <div className="vr-loading-text">加载全景图中 {progress}%</div>
                    <div className="vr-loading-bar">
                        <div className="vr-loading-bar-fill" style={{ width: `${progress}%` }} />
                    </div>
                </div>
            )}

            {/* 3D 场景 */}
            <div
                className="vr-canvas"
                ref={containerRef}
                style={{ opacity: loaded ? 1 : 0 }}
            />

            {/* 导航栏 */}
            {loaded && (
                <>
                    <div className="vr-nav">
                        {Object.entries(SCENE_CONFIG).map(([key, config]) => (
                            <button
                                key={key}
                                className={`vr-nav-btn${currentScene === key ? ' active' : ''}`}
                                onClick={() => handleNavClick(key)}
                            >
                                {config.label}
                            </button>
                        ))}
                    </div>
                    <div className="vr-hint">{SCENE_CONFIG[currentScene]?.label}全景</div>
                </>
            )}
        </div>
    );
};

export default VR;
